import {
  BAY_WIDTH, BAY_HEIGHT, BAY_SPACING,
  PARKING_WIDTH, PARKING_HEIGHT, PARKING_GAP,
  ROW_GAP, SECTION_GAP, GRID_SIZE,
} from '@/components/facility/constants';
import type { FacilityEntity, FacilityEntityType } from '@/types/facility';
import { ENTITY_RENDER_CONFIG } from '@/types/facility';


// ─── helpers ──────────────────────────────────────────
function uid(): string {
  return crypto.randomUUID();
}

function makeBay(
  type: FacilityEntityType,
  x: number, y: number,
  shopId: string,
  index: number,
): FacilityEntity {
  const cfg = ENTITY_RENDER_CONFIG[type];
  return {
    id: uid(),
    // Editor-generated entities (incl. parking rows) persist via shop_bays.
    storageSource: 'shop_bay',
    entityType: type,
    label: `${cfg.displayLabel} ${index + 1}`,
    x, y,
    width: cfg.defaultWidth,
    height: cfg.defaultHeight,
    isOutdoor: type === 'parking_space',
    isActive: true,
    isLockedOut: false,
    lockoutReason: null,
    isCommunity: false,
    assignedTechId: null,
    teamId: null,
    shopId,
    bayNumber: index + 1,
    dbBayType: type === 'parking_space' ? 'general' : (
      type === 'lift_2post' ? '2-post' :
      type === 'lift_4post' ? '4-post' :
      type === 'lift_scissor' ? 'scissor' :
      type === 'alignment' ? 'alignment' :
      type === 'adas' ? 'adas' :
      type === 'alignment_adas' ? 'alignment-adas' :
      type === 'wash' ? 'wash' :
      type === 'inspection' ? 'inspection' :
      'flat'
    ),
    dbBayCategory: type === 'parking_space' ? 'parking' : 'general',
  };
}

// ─── build ordered type list ──────────────────────────
function buildTypeList(
  standardCount: number,
  specialty: { alignment: boolean; adas: boolean; combo: boolean },
): FacilityEntityType[] {
  const types: FacilityEntityType[] = [];
  for (let i = 0; i < standardCount; i++) types.push('lift_2post');
  if (specialty.alignment) types.push('alignment');
  if (specialty.adas) types.push('adas');
  if (specialty.combo) types.push('alignment_adas');
  return types;
}

// ─── center layout at origin ──────────────────────────
// The centering offset is snapped to GRID_SIZE so a layout built from
// grid-aligned sizes/spacings stays on the grid (a raw half-extent can land
// on a 10 px boundary).
const snapToGridSize = (v: number) => Math.round(v / GRID_SIZE) * GRID_SIZE;
function centerEntities(entities: FacilityEntity[]): FacilityEntity[] {
  if (entities.length === 0) return entities;
  const minX = Math.min(...entities.map(e => e.x));
  const maxX = Math.max(...entities.map(e => e.x + e.width));
  const minY = Math.min(...entities.map(e => e.y));
  const maxY = Math.max(...entities.map(e => e.y + e.height));
  const cx = snapToGridSize((minX + maxX) / 2);
  const cy = snapToGridSize((minY + maxY) / 2);
  return entities.map(e => ({ ...e, x: e.x - cx, y: e.y - cy }));
}

// ─── generators ───────────────────────────────────────

export function generateStraightLine(
  standardCount: number,
  specialty: { alignment: boolean; adas: boolean; combo: boolean },
  shopId: string,
): FacilityEntity[] {
  const types = buildTypeList(standardCount, specialty);
  let x = 0;
  const entities = types.map((type, i) => {
    const cfg = ENTITY_RENDER_CONFIG[type];
    const bay = makeBay(type, x, 0, shopId, i);
    x += cfg.defaultWidth + BAY_SPACING;
    return bay;
  });
  return centerEntities(entities);
}

export function generateTwoRows(
  standardCount: number,
  specialty: { alignment: boolean; adas: boolean; combo: boolean },
  shopId: string,
): FacilityEntity[] {
  const types = buildTypeList(standardCount, specialty);
  const half = Math.ceil(types.length / 2);
  const topRow = types.slice(0, half);
  const bottomRow = types.slice(half);

  const entities: FacilityEntity[] = [];
  let x = 0;
  topRow.forEach((type, i) => {
    const cfg = ENTITY_RENDER_CONFIG[type];
    entities.push(makeBay(type, x, 0, shopId, i));
    x += cfg.defaultWidth + BAY_SPACING;
  });

  // Bottom row starts below the tallest top-row entity (never above the
  // canonical BAY_HEIGHT line), so taller entities can never overlap it.
  const bottomY = Math.max(BAY_HEIGHT, ...topRow.map(t => ENTITY_RENDER_CONFIG[t].defaultHeight)) + ROW_GAP;
  x = 0;
  bottomRow.forEach((type, i) => {
    const cfg = ENTITY_RENDER_CONFIG[type];
    entities.push(makeBay(type, x, bottomY, shopId, half + i));
    x += cfg.defaultWidth + BAY_SPACING;
  });

  return centerEntities(entities);
}

export function generateUShape(
  standardCount: number,
  specialty: { alignment: boolean; adas: boolean; combo: boolean },
  shopId: string,
): FacilityEntity[] {
  return layoutUShape(buildTypeList(standardCount, specialty), shopId);
}

/**
 * U-shape placement for an ordered type list: first third down the left
 * column, next third along the bottom, the rest up the right column.
 * Exported so arbitrary orderings (e.g. a wide specialty bay last in the
 * bottom row) can be verified directly.
 */
export function layoutUShape(types: FacilityEntityType[], shopId: string): FacilityEntity[] {
  const third = Math.ceil(types.length / 3);
  const left = types.slice(0, third);
  const bottom = types.slice(third, third * 2);
  const right = types.slice(third * 2);

  const entities: FacilityEntity[] = [];
  let idx = 0;
  const size = (t: FacilityEntityType) => ENTITY_RENDER_CONFIG[t];

  // All placement is cumulative over ACTUAL entity sizes, so mixed
  // standard / specialty footprints never overlap.

  // Left column (vertical, top-down)
  let y = 0;
  left.forEach(type => {
    entities.push(makeBay(type, 0, y, shopId, idx++));
    y += size(type).defaultHeight + BAY_SPACING;
  });
  const leftColumnWidth = Math.max(BAY_WIDTH, ...left.map(t => size(t).defaultWidth));

  // Bottom row (horizontal), below the left column and right of it
  const bottomY = y;
  let x = leftColumnWidth + BAY_SPACING;
  bottom.forEach(type => {
    entities.push(makeBay(type, x, bottomY, shopId, idx++));
    x += size(type).defaultWidth + BAY_SPACING;
  });

  // Right column (vertical, going up), BAY_SPACING beyond the bottom row's
  // actual right edge; stacked upward from just above the bottom row.
  const rightX = x;
  let rightY = bottomY;
  right.forEach(type => {
    rightY -= size(type).defaultHeight + BAY_SPACING;
    entities.push(makeBay(type, rightX, rightY, shopId, idx++));
  });

  return centerEntities(entities);
}

export function generateParkingRow(
  count: number,
  orientation: 'horizontal' | 'vertical',
  startX: number,
  startY: number,
  shopId: string,
  // 1-based starting number for the row's labels. The caller is expected
  // to compute this via `nextLabelNumber(entities, 'parking_space')` so a
  // fresh row picks up where existing spots leave off — without this,
  // every new row restarted at "Parking 1" and collided with prior spots,
  // forcing the mirror trigger to fall back to its `#<hex>` suffix.
  startNumber: number = 1,
  // Space between adjacent spots along the row. Defaults to the canonical
  // PARKING_GAP; an explicit operator value (ParkingRowDialog "Gap (px)")
  // is honored as-is, even when it is not a GRID_SIZE multiple.
  gap: number = PARKING_GAP,
): FacilityEntity[] {
  // `orientation` is the ROW's placement direction (left-to-right vs
  // top-to-bottom). Each spot keeps the canonical vertical
  // PARKING_WIDTH×PARKING_HEIGHT size either way; a spot's own
  // orientation only changes via an explicit Flip.
  const groupId = crypto.randomUUID();
  const entities: FacilityEntity[] = [];
  for (let i = 0; i < count; i++) {
    const x = orientation === 'horizontal'
      ? startX + i * (PARKING_WIDTH + gap)
      : startX;
    const y = orientation === 'vertical'
      ? startY + i * (PARKING_HEIGHT + gap)
      : startY;
    // makeBay's `index` param is 0-based and produces label `<displayLabel> ${index + 1}`,
    // so to land on `startNumber + i` we feed it `startNumber - 1 + i`.
    entities.push({
      ...makeBay('parking_space', x, y, shopId, startNumber - 1 + i),
      groupId,
    });
  }
  return entities;
}

// ─── full wizard generator ────────────────────────────
export function generateWizardLayout(
  shopId: string,
  config: {
    standardBays: number;
    specialty: { alignment: boolean; adas: boolean; combo: boolean };
    layout: 'straight' | 'two_rows' | 'u_shape';
    frontParking: number;
    rearParking: number;
  },
): FacilityEntity[] {
  // Work bays
  let bays: FacilityEntity[];
  switch (config.layout) {
    case 'two_rows':
      bays = generateTwoRows(config.standardBays, config.specialty, shopId);
      break;
    case 'u_shape':
      bays = generateUShape(config.standardBays, config.specialty, shopId);
      break;
    default:
      bays = generateStraightLine(config.standardBays, config.specialty, shopId);
  }

  // Compute work bay bounds for parking placement
  const allEntities = [...bays];
  if (bays.length > 0) {
    const minX = Math.min(...bays.map(e => e.x));
    const maxY = Math.max(...bays.map(e => e.y + e.height));
    const minY = Math.min(...bays.map(e => e.y));

    // Front parking (above bays)
    if (config.frontParking > 0) {
      const parkingRow = generateParkingRow(
        config.frontParking, 'horizontal',
        minX, minY - SECTION_GAP - PARKING_HEIGHT,
        shopId,
      );
      allEntities.push(...parkingRow);
    }

    // Rear parking (below bays)
    if (config.rearParking > 0) {
      const parkingRow = generateParkingRow(
        config.rearParking, 'horizontal',
        minX, maxY + SECTION_GAP,
        shopId,
      );
      allEntities.push(...parkingRow);
    }
  }

  return allEntities;
}
